import BaseApiService from './BaseApiService'

/** Where a document was opened. Views are only recorded on these four surfaces. */
export type ViewSurface = 'share' | 'preview' | 'esign' | 'embed'
export type ViewEvent = 'view' | 'download'

export interface AnalyticsTotals {
  documents: number
  pages: number
  failed: number
  /** 0..1, or null when nothing was rendered. */
  successRate: number | null
  views: number
  downloads: number
  /** Visitor ids rotate monthly, so this counts distinct viewers within a month window. */
  uniqueViewers: number
  viewedDocuments: number
  avgFirstViewHours: number | null
}

export interface AnalyticsPrevious {
  documents: number
  pages: number
  views: number
  uniqueViewers: number
}

export interface AnalyticsDay {
  day: string
  documents: number
  pages: number
  failed: number
  views: number
  downloads: number
  uniqueViewers: number
}

export interface FormatBreakdown { format: string; documents: number; pages: number }
export interface SurfaceBreakdown { surface: ViewSurface; views: number; downloads: number; uniqueViewers: number }
export interface DeviceBreakdown { device: string; views: number }
export interface BrowserBreakdown { browser: string; views: number }
export interface ReferrerBreakdown { domain: string; views: number }

export interface TopTemplate {
  templateId: string
  name: string
  documents: number
  pages: number
  views: number
  uniqueViewers: number
  failed: number
}

export interface AnalyticsOverview {
  range: { from: string; to: string; days: number }
  totals: AnalyticsTotals
  previous: AnalyticsPrevious
  daily: AnalyticsDay[]
  byFormat: FormatBreakdown[]
  bySurface: SurfaceBreakdown[]
  devices: DeviceBreakdown[]
  browsers: BrowserBreakdown[]
  referrers: ReferrerBreakdown[]
  topTemplates: TopTemplate[]
}

export interface TemplateAnalyticsRow {
  templateId: string
  name: string
  folderId: string | null
  documents: number
  pages: number
  failed: number
  views: number
  downloads: number
  uniqueViewers: number
  lastRenderedAt: string | null
  lastViewedAt: string | null
}

export interface VersionBreakdown {
  templateVersionId: string
  versionNumber: number
  documents: number
  failed: number
}

export interface TemplateAnalytics {
  template: { templateId: string; name: string }
  totals: AnalyticsTotals
  daily: AnalyticsDay[]
  byVersion: VersionBreakdown[]
  bySurface: SurfaceBreakdown[]
  devices: DeviceBreakdown[]
}

export interface DocumentViewEvent {
  occurredAt: string
  event: ViewEvent
  surface: ViewSurface
  device: string | null
  browser: string | null
  referrerDomain: string | null
  /** 8-char rotating visitor id. */
  visitor: string | null
}

export interface DocumentAnalytics {
  renderJobId: string
  templateId: string | null
  templateName: string | null
  createdAt: string
  pages: number | null
  views: number
  downloads: number
  uniqueViewers: number
  firstViewedAt: string | null
  lastViewedAt: string | null
  events: DocumentViewEvent[]
}

export interface RecentView {
  occurredAt: string
  event: ViewEvent
  surface: ViewSurface
  device: string | null
  browser: string | null
  renderJobId: string
  templateId: string | null
  templateName: string | null
  visitor: string | null
}

export type AdoptionFeature = 'share_links' | 'preview_links' | 'esign' | 'embed' | 'approvals' | 'connectors'

export interface PlatformAnalytics {
  totals: { teams: number; activeTeams: number; documents: number; pages: number; failed: number; views: number; uniqueViewers: number }
  daily: { day: string; documents: number; pages: number; views: number; activeTeams: number }[]
  topTeams: { teamId: string; name: string; plan: string | null; documents: number; pages: number; views: number; failed: number }[]
  byFormat: FormatBreakdown[]
  bySurface: SurfaceBreakdown[]
  featureAdoption: { feature: AdoptionFeature; teams: number }[]
}

class AnalyticsApiService extends BaseApiService {
  getOverview(days = 30): Promise<AnalyticsOverview> {
    return this.post('/v1/analytics/overview', { days })
  }

  getTemplates(days = 30): Promise<TemplateAnalyticsRow[]> {
    return this.post('/v1/analytics/templates', { days })
  }

  getTemplate(templateId: string, days = 30): Promise<TemplateAnalytics> {
    return this.post('/v1/analytics/template', { templateId, days })
  }

  getDocument(renderJobId: string): Promise<DocumentAnalytics> {
    return this.post('/v1/analytics/document', { renderJobId })
  }

  getRecentViews(limit = 50): Promise<RecentView[]> {
    return this.post('/v1/analytics/recent-views', { limit })
  }
}

export const analyticsApiService = new AnalyticsApiService()
